import React from 'react';
import { Mail, Eye, MousePointer, AlertCircle } from 'lucide-react';

const RateIndicator = ({ rate, type = 'open' }) => {
  const getColor = () => {
    if (type === 'click') return rate >= 5 ? 'text-green-400' : rate >= 2 ? 'text-yellow-400' : 'text-swp-white-ghost';
    return rate >= 30 ? 'text-green-400' : rate >= 15 ? 'text-yellow-400' : 'text-swp-white-ghost';
  };
  return <span className={`font-mono ${getColor()}`}>{rate}%</span>;
};

const DashboardCampaigns = ({ campaigns, formatDate }) => (
  <div className="space-y-6">
    {/* Campaign Stats */}
    <div className="grid grid-cols-3 gap-4">
      <div className="bg-swp-surface border border-swp-border rounded-swp p-4 text-center">
        <Mail size={24} className="text-swp-ice mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{campaigns.length}</p>
        <p className="text-swp-white-ghost/70 text-sm">Total Campaigns</p>
      </div>
      <div className="bg-swp-surface border border-swp-border rounded-swp p-4 text-center">
        <Eye size={24} className="text-green-400 mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{campaigns.reduce((acc, c) => acc + c.opened, 0)}</p>
        <p className="text-swp-white-ghost/70 text-sm">Total Opens</p>
      </div>
      <div className="bg-swp-surface border border-swp-border rounded-swp p-4 text-center">
        <MousePointer size={24} className="text-purple-400 mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{campaigns.reduce((acc, c) => acc + c.clicked, 0)}</p>
        <p className="text-swp-white-ghost/70 text-sm">Total Clicks</p>
      </div>
    </div>

    {/* Campaigns Table */}
    {campaigns.length === 0 ? (
      <div className="text-center py-12 bg-swp-surface border border-swp-border rounded-swp">
        <Mail className="w-12 h-12 text-gray-700 mx-auto mb-4" />
        <p className="text-swp-white-ghost">No email campaigns sent yet</p>
        <p className="text-swp-white-ghost/50 text-sm mt-2">Send your first newsletter from Studio &gt; Comms</p>
      </div>
    ) : (
      <div className="bg-swp-surface border border-swp-border rounded-swp overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-swp-border">
              {['Campaign', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Open Rate', 'Click Rate'].map(h => (
                <th key={h} className="text-left px-6 py-4 text-swp-white-ghost font-mono text-xs uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr key={campaign.id} className="border-b border-swp-border/50 hover:bg-swp-deep/50 transition-colors">
                <td className="px-6 py-4">
                  <p className="text-white font-medium truncate max-w-[200px]">{campaign.subject}</p>
                  <p className="text-swp-white-ghost/70 text-xs mt-1">{formatDate(campaign.sent_at)}</p>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-white">{campaign.sent}</span>
                  {campaign.failed > 0 && <span className="text-red-400 text-xs ml-1">({campaign.failed} failed)</span>}
                </td>
                <td className="px-4 py-4 text-center text-green-400">{campaign.delivered}</td>
                <td className="px-4 py-4 text-center text-blue-400">{campaign.opened}</td>
                <td className="px-4 py-4 text-center text-purple-400">{campaign.clicked}</td>
                <td className="px-4 py-4 text-center"><RateIndicator rate={campaign.open_rate} /></td>
                <td className="px-4 py-4 text-center"><RateIndicator rate={campaign.click_rate} type="click" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Webhook Info */}
    <div className="bg-swp-deep/70 border border-swp-border rounded-swp p-4">
      <div className="flex items-start gap-3">
        <AlertCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-200 text-sm font-medium">Enable Email Tracking</p>
          <p className="text-swp-white-ghost text-xs mt-1">To track opens and clicks, set up a webhook in your Postmark dashboard pointing to:</p>
          <code className="block mt-2 text-xs bg-swp-black px-3 py-2 rounded text-swp-ice">https://www.shadowwolvesproductions.com.au/api/webhooks/postmark</code>
          <p className="text-swp-white-ghost/70 text-xs mt-2">Select events: email.delivered, email.opened, email.clicked, email.bounced</p>
        </div>
      </div>
    </div>
  </div>
);

export default DashboardCampaigns;
